import urql, {
  dedupExchange,
  fetchExchange
  // cacheExchange
} from '@urql/vue'
import { authExchange } from '@urql/exchange-auth'
import { makeOperation } from '@urql/core'
import { cacheExchange } from '@urql/exchange-graphcache'
import { devtoolsExchange } from '@urql/devtools'

import { pipe, filter, merge, share } from 'wonka'
import { userHasRequestedACL, EDITOR_ACL } from './CMSAuth'
import { monogridCMSPagination } from './utils/pagination'
import { globalFetchingExchange } from './utils/globalFetchingExchange'
import { ref, computed } from '@vue/reactivity'

const cacheConfig = {
  resolvers: { Query: {} },
  keys: {}
}

const createCacheResolverExchange = ({ client, forward }) => {
  return operations$ => {
    // <-- The ExchangeIO function
    const shared = pipe(operations$, share)
    const queries = pipe(
      shared,
      filter(op => {
        op.query.definitions.forEach(def => {
          if (def.name.value.indexOf('List') === 0) {
            // list operation
            const resolver = cacheConfig.resolvers.Query[def.name.value]
            if (!resolver) {
              cacheConfig.resolvers.Query[def.name.value] = monogridCMSPagination(def)
            }
          }
        })
        return op.kind === 'query'
      })
    )
    const others = pipe(
      shared,
      filter(op => op.kind !== 'query')
    )
    return forward(merge([queries, others]))
  }
}

const getAuth = (CMSAuth) => async ({ authState }) => {
  return CMSAuth
}

const willAuthError = ({ authState }) => {
  if (!authState.user) return true
  // TODO: check for expiration, existence of auth etc
  return false
}

const addAuthToOperation = ({ authState, operation }) => {
  if (!authState || !authState.cognitoUser.value) {
    return operation
  }

  const fetchOptions =
    typeof operation.context.fetchOptions === 'function'
      ? operation.context.fetchOptions()
      : operation.context.fetchOptions || {}

  if (operation.variables.status && userHasRequestedACL(authState.cognitoUser.value, EDITOR_ACL)) {
    operation.variables.status = 'DRAFT'
  }

  return makeOperation(operation.kind, operation, {
    ...operation.context,
    fetchOptions: {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        authorization: `Bearer ${authState.cognitoUser.value.signInUserSession.accessToken.jwtToken}`
      }
    }
  })
}

const didAuthError = ({ error }) => {
  return error.graphQLErrors.some(e => e.extensions?.code === 'UNAUTHORIZED_ERROR')
}

const inflightCount = ref(0)
const globalLoadingStatus = computed(() => {
  // console.log('Inflight graphql request count', inflightCount.value)
  return inflightCount.value > 0
})

export const provideCMSClient = (env, CMSAuth) => {
  if (!env) throw new Error('Please provide a process.env object to this function that contains at least a CONTENT_API_ENDPOINT variable')

  const baseApiURI = env.IS_OFFLINE ? `${env.API_ENDPOINT}${env.SERVICE_STAGE}` : ''

  const client = {
    url: baseApiURI + env.CONTENT_API_ENDPOINT,
    exchanges: [
      devtoolsExchange,
      dedupExchange,
      authExchange({
        getAuth: getAuth(CMSAuth),
        addAuthToOperation,
        didAuthError,
        willAuthError
      }),
      createCacheResolverExchange,
      cacheExchange(cacheConfig),
      // cacheExchange,
      globalFetchingExchange(() => { inflightCount.value += 1 }, () => { inflightCount.value -= 1 }),
      fetchExchange
    ]
  }

  return { urql, client, globalLoadingStatus }
}
